import { OpenHours } from '../../entities/dealer-response';
import { OpeningHours } from '../../entities/content';
import { isString } from '../helper';
import { navContentStore } from '../../../shared/state/nav-content-store';

export function showDealerSearchDropdown(country: string): boolean {
  const countriesWithDropdown = ['CN'];

  return countriesWithDropdown.includes(country);
}

export function getRegionSpecificCountry(country: string): string {
  return country.includes('-') ? country.split('-')[0] : country;
}

export function shortenDealerHomepageText(originalUrl: string): string {
  return originalUrl.startsWith('https://') ? originalUrl.replace('https://', '') : originalUrl.replace('http://', '');
}

export function sanitizeDealerHomepage(originalUrl: string): string {
  try {
    new URL(originalUrl);
    return originalUrl;
  } catch (_) {
    return `http://${originalUrl}`;
  }
}

export function getOpeningHours(openingHoursData: OpenHours[]): string {
  const { openingHours } = navContentStore.state.dealer;

  if (!openingHours) {
    return '';
  }

  const WEEK_DAYS = [
    openingHours.monday,
    openingHours.tuesday,
    openingHours.wednesday,
    openingHours.thursday,
    openingHours.friday,
    openingHours.saturday,
    openingHours.sunday
  ];

  const openingHoursMap = getOpeningHoursMap(openingHours, openingHoursData);
  const result: string[] = [];
  let previousOpeningHours = '';
  let previousDayIndex = -1;
  let consecutiveDays: string[] = [];

  for (const day of WEEK_DAYS) {
    const currentDayIndex = WEEK_DAYS.indexOf(day);
    const openHours = openingHoursMap.get(day);
    const openingHoursString = getOpeningHoursString(openHours);

    if (openingHoursString !== '') {
      if (previousOpeningHours === '') {
        previousOpeningHours = openingHoursString;
        consecutiveDays.push(day);
      } else if (previousOpeningHours !== openingHoursString || previousDayIndex !== currentDayIndex - 1) {
        result.push(getDayRange(consecutiveDays, previousOpeningHours));
        consecutiveDays = [day];
        previousOpeningHours = openingHoursString;
      } else {
        consecutiveDays.push(day);
      }
      previousDayIndex = currentDayIndex;
    }
  }

  if (previousOpeningHours !== '') {
    result.push(getDayRange(consecutiveDays, previousOpeningHours));
  }

  return result.join('\n');
}

function getDayRange(days: string[], openingHoursString: string) {
  if (days.length === 1) {
    return `${days[0]}: ${openingHoursString}`;
  }
  return `${days[0]} - ${days[days.length - 1]}: ${openingHoursString}`;
}

function getOpeningHoursMap(openingHours: OpeningHours, openingHoursData: OpenHours[]) {
  const openingHoursMap: Map<string, { open: string; close: string }> = new Map();

  for (const item of openingHoursData) {
    const day = openingHours[item.day.toLowerCase() as keyof OpeningHours];

    if (isString(day)) {
      openingHoursMap.set(day, { open: item.open, close: item.close });
    }
  }

  return openingHoursMap;
}

function getOpeningHoursString(openHours: { open: string; close: string } | undefined): string {
  return openHours && openHours.open !== '' && openHours.close !== '' ? getTimeFormat(openHours) : '';
}

function getTimeFormat(openHours: { open: string; close: string }): string {
  const { openingHours } = navContentStore.state.dealer;

  const timeFormat = openingHours?.timeFormat === 12;

  return `${convertToLocalTime(openHours.open, timeFormat)} - ${convertToLocalTime(openHours.close, timeFormat)}`;
}

function convertToLocalTime(time: string, timeFormat = false) {
  const [hour, minute] = time.split(':').map(Number);
  const date = new Date(0, 0, 0, hour, minute);

  return date
    .toLocaleString(undefined, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: timeFormat
    })
    .toUpperCase();
}
